export type IdentitySelection = {
  semesterId: string;
  classId: string;
  courseId: string;
};

type SelectionClass = {
  classId: string;
  courses: readonly { courseId: string }[];
  lessonSchedule?: readonly { semester?: { semesterId: string } | null }[] | null;
};

export type IdentitySelectionChange = {
  type: 'semester' | 'class' | 'course';
  id: string;
};

// Resolve the whole selection together. A course change may cross semesters;
// filtering classes by the old semester first would leave the old class selected.
export function resolveIdentitySelection(
  classes: readonly SelectionClass[],
  semesterIds: readonly string[],
  courseIds: readonly string[],
  saved: IdentitySelection,
  change?: IdentitySelectionChange,
): IdentitySelection {
  const sorted = [...classes].sort((a, b) => a.classId.localeCompare(b.classId));
  const semestersFor = (clas: SelectionClass) =>
    semesterIds.filter((id) =>
      clas.lessonSchedule?.some((schedule) => schedule.semester?.semesterId === id),
    );
  const eligible = sorted.filter((clas) => !semesterIds.length || semestersFor(clas).length);
  const hasCourse = (clas: SelectionClass, id: string) =>
    courseIds.includes(id) && clas.courses.some((course) => course.courseId === id);
  const inSemester = (clas: SelectionClass, id: string) => semestersFor(clas).includes(id);
  const preferredClass = (candidates: SelectionClass[]) =>
    candidates.find(
      (clas) => clas.classId === saved.classId && inSemester(clas, saved.semesterId),
    ) ??
    candidates.find((clas) => inSemester(clas, saved.semesterId)) ??
    candidates.find((clas) => clas.classId === saved.classId) ??
    candidates[0];

  let selected: SelectionClass | undefined;
  let semesterId = saved.semesterId;
  let courseId = saved.courseId;

  if (change?.type === 'semester' && semesterIds.includes(change.id)) {
    semesterId = change.id;
    const candidates = eligible.filter((clas) => inSemester(clas, semesterId));
    selected =
      candidates.find((clas) => clas.classId === saved.classId) ??
      candidates.find((clas) => hasCourse(clas, courseId)) ??
      candidates[0];
  } else if (change?.type === 'class') {
    selected = eligible.find((clas) => clas.classId === change.id);
  } else {
    if (change?.type === 'course' && courseIds.includes(change.id)) courseId = change.id;
    // Also repair old persisted combinations on the first render, before consumers
    // can display a new course with the previous class's time or build a wrong URL.
    selected = preferredClass(eligible.filter((clas) => hasCourse(clas, courseId)));
  }

  selected ??= preferredClass(eligible);
  if (!selected) return { semesterId: '', classId: '', courseId: '' };
  const classSemesters = semestersFor(selected);
  if (!classSemesters.includes(semesterId)) semesterId = classSemesters[0] ?? '';
  if (!hasCourse(selected, courseId)) {
    courseId = courseIds.find((id) => hasCourse(selected!, id)) ?? '';
  }
  return { semesterId, classId: selected.classId, courseId };
}
